<script lang="ts">
  import { getContext } from 'svelte';
  import type { Image } from '$lib/sanity/schemas';
  import { type SanityContext, key } from '$lib/context/sanity';
  import { pipe } from '$lib/utils/pipe';
  import { toCSSVars } from '$lib/utils/cssVars';
  import { extractAspectRatioFromAsset } from '$lib/utils/sanity';
  import {
    useSanityImageLazyload,
    toSrcsetUrlBuilders,
    toLazyloadSrcsetUrlBuilders,
    toImageSrcAttributes
  } from '$lib/sanity/image';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';

  interface Props {
    document: Image;
    sourceMap: ContentSourceMap;
    path?: StudioPathLike;
  }

  let { document, sourceMap, path = [] }: Props = $props();

  const { urlBuilder } = getContext<SanityContext>(key);
  const { isInView, lazyload } = useSanityImageLazyload();

  let encodeDataAttribute = $derived(useEncodeDataAttribute(document, sourceMap, sanityStudioURL));
  let srcAttrs = $derived(
    document?.image?.asset &&
      pipe(
        document.image.asset,
        toSrcsetUrlBuilders(urlBuilder),
        toLazyloadSrcsetUrlBuilders($isInView),
        toImageSrcAttributes()
      )
  );
  let aspectRatio = $derived(document.image && extractAspectRatioFromAsset(document.image));
</script>

{#if srcAttrs}
  <img
    use:lazyload
    class="lazy"
    srcset={srcAttrs.srcset}
    src={srcAttrs.src}
    sizes="100vw"
    alt={document?.image?.asset.altText}
    style={toCSSVars({ aspectRatio })}
    data-sanity={encodeDataAttribute([...path, 'imageRef'])}
  />
{/if}

<style lang="scss">
  @import 'src/scss/lazyload';

  img {
    @include lazyload-blur;

    aspect-ratio: var(--aspect-ratio);
    width: 100%;
    border-radius: var(--theme-general-image-border-radius, inherit);
  }
</style>
